/**
 * 动画 Design Token 系统
 * 定义完整的动画效果规范
 */

import type { AnimationTokens } from './types';

// 动画 Token 定义
export const animationTokens: AnimationTokens = {
  // 动画持续时间
  duration: {
    instant: 0,         // 0ms - 瞬间变化
    fast: 150,          // 150ms - 快速动画
    base: 200,          // 200ms - 标准动画
    slow: 300,          // 300ms - 慢速动画
    slower: 500,        // 500ms - 更慢动画
    slowest: 800,       // 800ms - 最慢动画
  },
  
  // 缓动函数
  easing: {
    linear: 'linear',                           // 线性缓动
    ease: 'ease',                              // 标准缓动
    easeIn: 'ease-in',                         // 缓入
    easeOut: 'ease-out',                       // 缓出
    easeInOut: 'ease-in-out',                  // 缓入缓出
    
    // 自定义贝塞尔曲线
    smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',    // 平滑过渡
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)', // 弹跳效果
    sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',     // 锐利过渡
    emphasized: 'cubic-bezier(0.2, 0, 0, 1)',  // 强调过渡
  },
};

// 动画使用规范
export const animationUsage = {
  // 持续时间应用场景
  durationUsage: {
    instant: '状态切换、开关切换、即时反馈',
    fast: '按钮悬停、图标变化、小元素动画',
    base: '页面切换、卡片翻转、标准交互',
    slow: '抽屉展开、模态框显示、复杂动画',
    slower: '页面加载、大型容器动画、品牌动画',
    slowest: '特殊效果、品牌展示、引导动画',
  },
  
  // 缓动函数应用场景
  easingUsage: {
    linear: '进度条、加载动画、匀速运动',
    ease: '通用动画、默认过渡效果',
    easeIn: '元素消失、淡出效果、收缩动画',
    easeOut: '元素出现、淡入效果、展开动画',
    easeInOut: '状态切换、双向动画、平衡过渡',
    smooth: '页面滚动、平滑切换、优雅过渡',
    bounce: '按钮点击、成功反馈、活泼效果',
    sharp: '快速切换、即时响应、锐利变化',
    emphasized: '重要动画、强调效果、品牌动画',
  },
};

// 预定义动画组合
export const animationPresets = {
  // 淡入淡出
  fade: {
    in: {
      duration: animationTokens.duration.base,
      easing: animationTokens.easing.easeOut,
      keyframes: 'fadeIn',
    },
    out: {
      duration: animationTokens.duration.base,
      easing: animationTokens.easing.easeIn,
      keyframes: 'fadeOut',
    },
  },
  
  // 滑动
  slide: {
    up: {
      duration: animationTokens.duration.base,
      easing: animationTokens.easing.smooth,
      keyframes: 'slideUp',
    },
    down: {
      duration: animationTokens.duration.base,
      easing: animationTokens.easing.smooth,
      keyframes: 'slideDown',
    },
    left: {
      duration: animationTokens.duration.base,
      easing: animationTokens.easing.smooth,
      keyframes: 'slideLeft',
    },
    right: {
      duration: animationTokens.duration.base,
      easing: animationTokens.easing.smooth,
      keyframes: 'slideRight',
    },
  },
  
  // 缩放
  scale: {
    in: {
      duration: animationTokens.duration.base,
      easing: animationTokens.easing.bounce,
      keyframes: 'scaleIn',
    },
    out: {
      duration: animationTokens.duration.fast,
      easing: animationTokens.easing.easeIn,
      keyframes: 'scaleOut',
    },
  },
  
  // 旋转
  rotate: {
    clockwise: {
      duration: animationTokens.duration.base,
      easing: animationTokens.easing.linear,
      keyframes: 'rotateClockwise',
    },
    counterclockwise: {
      duration: animationTokens.duration.base,
      easing: animationTokens.easing.linear,
      keyframes: 'rotateCounterclockwise',
    },
  },
  
  // 弹跳
  bounce: {
    in: {
      duration: animationTokens.duration.slow,
      easing: animationTokens.easing.bounce,
      keyframes: 'bounceIn',
    },
    out: {
      duration: animationTokens.duration.base,
      easing: animationTokens.easing.easeIn,
      keyframes: 'bounceOut',
    },
  },
  
  // 摇摆
  shake: {
    horizontal: {
      duration: animationTokens.duration.slow,
      easing: animationTokens.easing.easeInOut,
      keyframes: 'shakeHorizontal',
    },
    vertical: {
      duration: animationTokens.duration.slow,
      easing: animationTokens.easing.easeInOut,
      keyframes: 'shakeVertical',
    },
  },
};

// 组件特定动画配置
export const componentAnimations = {
  // 按钮动画
  button: {
    hover: {
      duration: animationTokens.duration.fast,
      easing: animationTokens.easing.easeOut,
      properties: ['transform', 'box-shadow', 'background-color'],
    },
    active: {
      duration: animationTokens.duration.instant,
      easing: animationTokens.easing.easeIn,
      properties: ['transform'],
    },
    focus: {
      duration: animationTokens.duration.fast,
      easing: animationTokens.easing.easeOut,
      properties: ['box-shadow'],
    },
  },
  
  // 卡片动画
  card: {
    hover: {
      duration: animationTokens.duration.base,
      easing: animationTokens.easing.smooth,
      properties: ['transform', 'box-shadow'],
    },
    appear: {
      duration: animationTokens.duration.slow,
      easing: animationTokens.easing.easeOut,
      properties: ['opacity', 'transform'],
    },
  },
  
  // 模态框动画
  modal: {
    enter: {
      duration: animationTokens.duration.slow,
      easing: animationTokens.easing.easeOut,
      properties: ['opacity', 'transform'],
    },
    exit: {
      duration: animationTokens.duration.base,
      easing: animationTokens.easing.easeIn,
      properties: ['opacity', 'transform'],
    },
  },
  
  // 抽屉动画
  drawer: {
    enter: {
      duration: animationTokens.duration.slow,
      easing: animationTokens.easing.smooth,
      properties: ['transform'],
    },
    exit: {
      duration: animationTokens.duration.base,
      easing: animationTokens.easing.easeIn,
      properties: ['transform'],
    },
  },
  
  // 下拉菜单动画
  dropdown: {
    enter: {
      duration: animationTokens.duration.base,
      easing: animationTokens.easing.easeOut,
      properties: ['opacity', 'transform'],
    },
    exit: {
      duration: animationTokens.duration.fast,
      easing: animationTokens.easing.easeIn,
      properties: ['opacity', 'transform'],
    },
  },
  
  // 工具提示动画
  tooltip: {
    enter: {
      duration: animationTokens.duration.fast,
      easing: animationTokens.easing.easeOut,
      properties: ['opacity'],
    },
    exit: {
      duration: animationTokens.duration.fast,
      easing: animationTokens.easing.easeIn,
      properties: ['opacity'],
    },
  },
  
  // 加载动画
  loading: {
    spin: {
      duration: animationTokens.duration.slowest,
      easing: animationTokens.easing.linear,
      properties: ['transform'],
      iteration: 'infinite',
    },
    pulse: {
      duration: animationTokens.duration.slower,
      easing: animationTokens.easing.easeInOut,
      properties: ['opacity'],
      iteration: 'infinite',
    },
  },
};

// 响应式动画系统
export const responsiveAnimations = {
  // 移动端动画调整 (减少动画时长)
  mobile: {
    duration: {
      instant: animationTokens.duration.instant,
      fast: 100,      // 减少快速动画时长
      base: 150,      // 减少标准动画时长
      slow: 200,      // 减少慢速动画时长
      slower: 300,    // 减少更慢动画时长
      slowest: 500,   // 减少最慢动画时长
    },
    easing: animationTokens.easing, // 缓动函数保持不变
  },
  
  // 平板端动画调整
  tablet: {
    duration: {
      instant: animationTokens.duration.instant,
      fast: 120,      // 略微减少快速动画时长
      base: 180,      // 略微减少标准动画时长
      slow: 250,      // 略微减少慢速动画时长
      slower: 400,    // 略微减少更慢动画时长
      slowest: 650,   // 略微减少最慢动画时长
    },
    easing: animationTokens.easing,
  },
  
  // 桌面端动画 (默认)
  desktop: animationTokens,
};

// 无障碍动画设置
export const accessibilityAnimations = {
  // 减少动画 (用户偏好设置)
  reducedMotion: {
    duration: {
      instant: 0,
      fast: 0,
      base: 0,
      slow: 0,
      slower: 0,
      slowest: 0,
    },
    easing: {
      all: 'ease',
    },
  },
  
  // 高对比度模式动画调整
  highContrast: {
    // 保持动画但简化效果
    simplifiedEffects: true,
    focusEnhancement: true,
  },
};

// 导出动画常量，用于 SCSS 变量兼容
export const animationConstants = {
  // 持续时间
  DURATION_INSTANT: animationTokens.duration.instant,
  DURATION_FAST: animationTokens.duration.fast,
  DURATION_BASE: animationTokens.duration.base,
  DURATION_SLOW: animationTokens.duration.slow,
  DURATION_SLOWER: animationTokens.duration.slower,
  DURATION_SLOWEST: animationTokens.duration.slowest,
  
  // 缓动函数
  EASING_LINEAR: animationTokens.easing.linear,
  EASING_EASE: animationTokens.easing.ease,
  EASING_EASE_IN: animationTokens.easing.easeIn,
  EASING_EASE_OUT: animationTokens.easing.easeOut,
  EASING_EASE_IN_OUT: animationTokens.easing.easeInOut,
  EASING_SMOOTH: animationTokens.easing.smooth,
  EASING_BOUNCE: animationTokens.easing.bounce,
  EASING_SHARP: animationTokens.easing.sharp,
  EASING_EMPHASIZED: animationTokens.easing.emphasized,
} as const;